import { useAuthContext } from "@hooks/context/useAuthContext";
import { useProfileContext } from "@hooks/context/useProfileContext";
import { useProjectContext } from "@hooks/context/useProjectContext";
import { useOrgSectionVisibility } from "@hooks/crud/organisationDashboard/useOrgSectionVisibility";
import { useOrganisationFrameworksContext } from "@hooks/context/useOrganisationFrameworksContext";
import { useSidebarContext } from "@hooks/context/useSidebarContext";
import { useListProject } from "@hooks/crud/project/useListProject";
import { crumbLabelFor } from "@layout/AppTopBar/AppTopBar.helpers";
import { SidebarLayoutType } from "@layout/MainSidebar/const";
import { MainSidebar } from "@layout/MainSidebar/MainSidebar";
import * as RouteHelper from "@utils/paths";
import { useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { hasLeftPaths, shouldCloseSidebar } from "../shouldCloseSidebar";
import { getNavMenuItems } from "./organisation-layout-menu-items/organisation-layout-menu-items";

const SIDEBAR_CLOSE_PATHS = [
  "/project-setup",
  "/employee-setup",
  "/sroi-builder/create"
];

/**
 * Where the Compliance rows can go stale under this wrapper's feet.
 *
 * Project set-up is where a framework is adopted or removed, so a row appears
 * or disappears. The Framework Library is where one is RENAMED, and the
 * project's `project_framework` row — which is what `listOrganisationFrameworks`
 * reads and what every sidebar shows — is a copy the save updates on the
 * server. Either way the list this wrapper fetched on mount is behind, and the
 * wrapper never unmounts to refetch it.
 */
const FRAMEWORK_STALE_PATHS = ["/project-setup", "/framework-library"];

interface Props {
  selectedItem: string;
  setSelectedItem: (item: string) => void;
}

const OrganisationLayoutSidebarWrapper = ({
  selectedItem,
  setSelectedItem
}: Props) => {
  const { isSocialProAdmin } = useAuthContext();
  const { isDrawOpen, setIsDrawOpen, setSectionLabel } = useSidebarContext();
  const { isAuthProfileLoading, userOrganisation, userProfile } =
    useProfileContext();
  const { isProjectLoading } = useProjectContext();
  const navigate = useNavigate();
  // The scope switcher lists every project from org scope too — one nav now
  // moves between the organisation and its projects, so the list the project
  // sidebar used to own is needed here as well.
  const { projects } = useListProject(userProfile, false);
  const switchProject = useCallback(
    (projectId: string, contractorPackageId: string) =>
      navigate(RouteHelper.contractorDashboard(projectId, contractorPackageId)),
    [navigate]
  );
  // Which Insights sections this org has commitments for. Read here rather than
  // in a context because the sidebar is the only consumer, and it is mounted
  // once for the whole org layout — one fetch per visit either way.
  const { data: sectionVisibility, isLoading: isVisibilityLoading } =
    useOrgSectionVisibility();
  // The Compliance rows: the frameworks the org's projects report against.
  // From the layout's context, NOT this component's own fetch, because the org
  // Compliance page reads the same list — two mounted consumers meant two
  // identical requests on every visit. The context owns the contractor gate
  // that used to live here.
  const isContractor = userOrganisation?.isContractor ?? false;
  const {
    frameworks,
    hasLoaded: hasLoadedFrameworks,
    refresh: refreshFrameworks
  } = useOrganisationFrameworksContext();
  const menuItems = getNavMenuItems(
    isSocialProAdmin,
    sectionVisibility,
    frameworks,
    isContractor,
    // Only once the list has arrived: an eager 0 would show a count the
    // organisation does not have and then correct itself.
    projects.length || undefined
  );
  const location = useLocation();

  /**
   * The whole menu waits for the Compliance rows, not just that section.
   *
   * They are the last thing to arrive — the fetch cannot start until the
   * profile resolves and says this is a contractor org — so a sidebar that
   * rendered as soon as the other three settled grew a section a moment later
   * and shoved everything below it down.
   *
   * `hasLoadedFrameworks`, not the hook's `isLoading`: that is false on the
   * first render, before there is an organisation id to fetch with, so gating
   * on it would render the menu, hide it and render it again. And gated on
   * `isContractor`, because a non-contractor organisation has no Compliance
   * section coming — waiting for one would hang its sidebar for good.
   */
  const loading =
    isAuthProfileLoading ||
    isProjectLoading ||
    isVisibilityLoading ||
    (isContractor && !hasLoadedFrameworks);

  // The top bar's trailing crumb: the group the page sits in, else the page's
  // own title (Settings is listed nowhere; it reads as itself).
  useEffect(() => {
    setSectionLabel(crumbLabelFor(menuItems, selectedItem));
  }, [menuItems, selectedItem, setSectionLabel]);

  // Refreshed on the transition OUT of those paths, not on every navigation:
  // moving between set-up steps or between Library pages refires nothing, and
  // ordinary sidebar navigation costs no extra requests.
  const previousPathname = useRef(location.pathname);
  useEffect(() => {
    if (shouldCloseSidebar(location.pathname, SIDEBAR_CLOSE_PATHS)) {
      setIsDrawOpen(false);
    }
    if (
      hasLeftPaths(
        previousPathname.current,
        location.pathname,
        FRAMEWORK_STALE_PATHS
      )
    ) {
      refreshFrameworks();
    }
    previousPathname.current = location.pathname;
  }, [location.pathname, refreshFrameworks]);

  return (
    <MainSidebar
      isDrawOpen={isDrawOpen}
      loading={loading}
      menuItems={menuItems}
      selectedItem={selectedItem}
      setIsDrawOpen={setIsDrawOpen}
      setSelectedItem={setSelectedItem}
      layoutType={SidebarLayoutType.ORGANISATION}
      projects={projects}
      switchProject={switchProject}
    />
  );
};

export default OrganisationLayoutSidebarWrapper;
